<template>
  <div class="pa-0 ma-0 d-flex align-center justify-center" v-bind:style="{background:bgColor}">
    <svg :id="rootElementId"></svg>
  </div>
</template>

<script>

import '../../utils/swoopy-drag';

import * as d3 from "d3v4";
import {getDataUrl } from '../../utils/functions';
import * as _ from "lodash";
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: "Beeswarm",
  props: {
    size: {
      type: Number,
      required: false
    },
    margin: {
      type: Object,
      // {top: 0, right: 0, bottom: 0, left: 0}
      required: false
    },
    playerId: {
      type: Number,
      required: true
    },
    playerSeasonInfo: {
      type: Number,
      required: true
    },
    highlightPlayersOfTeamId: {
      type: Array,
      required: false
    },
    highlightPlayersByPlayerId: {
      type: Array,
      required: false
    },
    excludeTeams:{
      type: Array,
      required: false
    },
    statId: {
      type: Number,
      required: false
    },
    stat: {
      type: String,
      required: false
    },
    reversedStat: {
      type: Boolean,
      default: false
    },
    idKey: {
      type: Number,
      required: true
    },
    darkMode: {
      type: Boolean,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    bgColor: {
      type: String,
      required: false
    },
    logoCfg: {
      type: String,
      default: ""
    },
    textColor: {
      type: String,
      default: ""
    },
    axisColor: {
      type: String,
      default: ""
    },
    mediaMode: {
      type: Boolean,
      default: false
    },
    dotColor:{
      type: String,
      default:""
    },
    comparisonDotColor:{
      type: String,
      default:""
    },
    comparisonPlayer:{
      type: Object,
      required: false
    },
    //chartData contains array of bees of BeeswarmParentDTO
    chartData:{
      type: Array,
      required: false
    },
    embed: {
      type: Boolean,
      default: false
    },
    doTimeOut: {
      type: Boolean,
      default: false
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    }
  },
  data() {
    return {
      ticks: 12,
      data: null,
      observer: null,
      forceCollide: 4,
      sizeOffset: 0.75,
      images: {},
      tooltipClass: null,
    };
  },
  computed: {
    rootElementId(){
      return `beeswarmSvg-${this.stat}`
    }
  },
  mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
    this.tooltipClass = this.darkMode ? 'tooltipDark' : 'tooltip'
    if(this.doTimeOut){
      setTimeout(() => { this.drawBeeswarm();}, 1);
    }else{
      this.drawBeeswarm();
    }
  },
  methods: {
    drawImage(svg, x, y, width, height, img,idName,bottom = false) {
      let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
    },
    measureText(string, fontSize = 10) {
      const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
        0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
        0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
        0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
        0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
        0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
        0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
        0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
      const avg = 0.5293256578947368;

      return string
        .split('')
        .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
        .reduce((cur, acc) => acc + cur) * fontSize;
    },
    drawBeeswarm(force = false) {
      const chartSize = this.size && this.size > 300 ? this.size : 300;
      let height = (chartSize / 2.7);
      let width = chartSize;
      let beesLength = this.chartData.length;
      const isMobile = this.$vuetify.breakpoint.smAndDown;
      let showWarning = false;
      //Over this value, value format to just one decimal
      const VALUE_OVER_WHICH_JUST_ONE_DECIMAL = 10;
      const margin = {
        top: 0,
        right: width / 30,
        bottom: 20,
        left: width / 30
      };
      const svgObject = document.getElementById(this.rootElementId);
      const svg = d3.select(svgObject);
      const statIsReversed = this.reversedStat;
      const xRange = statIsReversed ? width - margin.left - margin.right : 0;
      const yRange = statIsReversed ? 0 : width - margin.left - margin.right;
      const x = d3
        .scaleLinear()
        .rangeRound([xRange, yRange]);

      this.data = [...this.chartData];
      //Get value from stats list of Stat to render and create a "value" field for each player (bee)
      this.data = this.data.map(player => {
        //Find value of stat to render
        let stat = player.stats.find(s => s.id === this.statId);
        let value = stat ? stat.v : null;
        //If can't find stat value, show warning
        if(!value && this.playerId === player.id)
          showWarning = true;

        return {...player, v: value};
      }).filter(p => p.v); //Remove players without value

      this.$emit("dataLoaded", this.data);

      this.ticks = isMobile ? 6 : 12;

      svg.selectAll("*").remove();
      svg
        .attr("width", width)
        .attr("height", height + 25);

      if (this.logoCfg != "none") {
        let logoWidth = width * 0.15;
        let logoHeight = logoWidth * 0.31;
        let yLogo = isMobile ? height - margin.bottom - (logoHeight * 2.5) : height - margin.bottom - (logoHeight * 2.5);

        this.drawImageBase64(svg,
          width - (logoWidth) - margin.left,
          yLogo,
          logoWidth,
          logoWidth,
          require(this.darkMode ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png"),
          '')
      }
      
      if(showWarning){
        const textElement = svg
          .append("text")
          .style("font-size", "12px")
          .style("font-family", this.fontFamily)
          .style("color", "white")
          .style("font-weight","600")
          .attr("text-anchor", "left")
          .attr("dy", "0.8em")
          .attr("x", 11)
          .attr("y", 45)
          .text(this.$t("playerDetail.warningBeeswarm"));

          // Get text bounding box after it's been appended
          const bbox = textElement.node().getBBox();

          // Append a rectangle as the background
          svg.insert("rect", "text") // Insert before text
              .attr("x", bbox.x - 2) // Add slight padding
              .attr("y", bbox.y - 2)
              .attr("width", bbox.width + 4) // Padding on sides
              .attr("height", bbox.height + 4)
              .attr("fill", "#fb8c00"); // Set background color
      }
      //Ifdata is not empty, print bees, else print warning message
      if(this.data.length){

        //Exclude players of Teams
        if(this.excludeTeams && this.excludeTeams.length > 0){
          //Get players with teamId not in Exclude Taams or Player selected (second condition)
          this.data = this.data
                      .filter(b => !this.excludeTeams.includes(b.teamId)
                              || b.pSI === Number(this.playerSeasonInfo));
        }

        //Sort bees ASC (less value first) by value
        this.data.sort((a, b) => {
            return a.v - b.v;
        });

        //Remove selected player from bees
        let player = _.remove(this.data, (d) => d.pSI === Number(this.playerSeasonInfo));
        if(player && player.length > 0){
          //Filter players with value null
          this.data = this.data.filter(p => p.v);
          //Add to tail of data to be rendered the last
          this.data.push(player[0]);
        }

        //If there is team to highlight players
        if(this.highlightPlayersOfTeamId){
          //Remove players of team from bees
          let players = _.remove(this.data, (d) => this.highlightPlayersOfTeamId.includes(d.teamId));
          //Add to tail of data to be rendered the last
          this.data = this.data.concat(players);
        }
        //If there are players to highlight
        if (this.highlightPlayersByPlayerId) {
          //Remove players from bees
          let players = _.remove(this.data, (d) => this.highlightPlayersByPlayerId.includes(d.id));
          //Add to tail of data to be rendered the last
          this.data = this.data.concat(players);
        }
        if(this.comparisonPlayer){
          let el = _.remove(this.data,(d) => d.id === this.comparisonPlayer.id && d.seasonId === this.comparisonPlayer.seasonId);
          this.data.push(el[0]);
        }

        //Calculate group of each bee
        beesLength = beesLength === this.data.length ? beesLength : this.data.length;
        this.data.forEach((bee, i) => {
          bee.group = Math.trunc(i * 5/beesLength);
        });

        x.domain(d3.extent(this.data, d => d.v));

        const simulation = d3
          .forceSimulation(this.data)
          .force("x", d3.forceX(d => x(d.v)).strength(1))
          .force("y", d3.forceY((height - margin.top - 20) / 2))
          .force("collide", d3.forceCollide(this.$vuetify.breakpoint.xlOnly ? this.forceCollide : this.forceCollide * this.sizeOffset))
          .stop();

        if (isMobile) {
          for (let i = 0; i < 60; ++i) simulation.tick();
        } else {
          for (let i = 0; i < 120; ++i) simulation.tick();
        }

        const g = svg
          .append("g")
          .attr("width", width)
          .attr("height", height - margin.top)
          .attr(
            "transform",
            "translate(" + margin.left + "," + margin.top + ")"
          );

        g.append("g")
          .attr("class", this.darkMode ? "axisDark axis--x" : "axis axis--x")
          .style("fill", "transparent")
          .style("color", this.axisColor)
          .attr("transform", "translate(0," + (height - margin.top - margin.bottom) + ")")
          .call(d3.axisBottom(x).ticks(this.ticks, ".3"));
        // Define the div for the tooltip
        if(d3.select(`body > .${this.tooltipClass}`).empty()){
          var div = d3
            .select("body")
            .append("div")
            .attr("class", this.tooltipClass)
            .attr("height", 0)
            .style("opacity", 0);
        }

        const cell = g
          .append("g")
          .attr("class", "cells")
          .attr("width", width)
          .attr("height", height - margin.top - margin.bottom)
          .style("fill", "transparent")
          .selectAll("g")
          .data(
            d3
              .voronoi()
              .extent([
                [-margin.left, -margin.top],
                [width, height - margin.top - margin.bottom]
              ])
              .x(d => d.x)
              .y(d => d.y)
              .polygons(this.data).filter(o => o.data.pSI)
          )
          .enter()
          .append("g");

        labels.forEach(function(d) {
            cell
              .filter(v => v.name == d.name)
              .selectAll("text.label")
              .data([d])
              .enter()
              .append("text.label.shadow")
              .html(d.name);

            cell
              .selectAll(".label")
              .filter(v => v.name == d.name)

            cell
              .filter(v => v.name == d.name)
              .selectAll("path")
              .data([d])
              .enter()
              .insert("path", "text")
              .attr("d", d.path)
              .style("fill", "none")
              .style("stroke", "#74736c");
        });

        var self = this;

        cell
          .append("circle")
          .attr("id",(d) => `beeCircle-${this.stat}-${d.data.pSI}`)
          .attr("r", function(d) {
            let base = (d.data.id === self.playerId)
            || ( self.comparisonPlayer &&
              d.data.id === self.comparisonPlayer.playerId &&
              d.data.seasonId === Number(self.comparisonPlayer.seasonId))
            || (self.highlightPlayersOfTeamId && self.highlightPlayersOfTeamId.includes(d.data.teamId))
            || (self.highlightPlayersByPlayerId && self.highlightPlayersByPlayerId.includes(d.data.id)) ? 10 : 5;
            base = self.$vuetify.breakpoint.xlOnly ? base : self.sizeOffset*base;
            return base;
          })
          .attr("cx", d => d.data.x)
          .attr("cy", d => d.data.y)
          .style("stroke", self.darkMode ? "#000000" : "#FFFFFF")
          .style("stroke-width", 1)
          .style("fill", function(d, i) {
            //Highlight Selected Player
            if (d.data.pSI === Number(self.playerSeasonInfo)){
              let color =self.darkMode ? "#FFFFFF" : "#000000";
              color = self.mediaMode ? self.dotColor : color;
              return color;
            }
            //Highlight Players of Team
            if (self.highlightPlayersOfTeamId && self.highlightPlayersOfTeamId.includes(d.data.teamId)){
              let color = self.darkMode ? "#FFFFFF" : "#6d7de5";
              color = self.mediaMode ? self.dotColor : color;
              return color;
            }
            //Highlight Players
            if (self.highlightPlayersByPlayerId && self.highlightPlayersByPlayerId.includes(d.data.id)){
              let color = self.darkMode ? "#FFFFFF" : "#6d7de5";
              color = self.mediaMode ? self.dotColor : color;
              return color;
            }
            //Highlight Compare Player
            if (self.comparisonPlayer &&
              d.data.id === self.comparisonPlayer.playerId &&
              d.data.seasonId === Number(self.comparisonPlayer.seasonId)
            ){
              let color = "#444";
              color = self.mediaMode ? self.comparisonDotColor : color;
              return color;
            }
            if (d.data.v < 0.01)
              return statIsReversed ? "#476F20" : "#F64030";
            if (d.data.group === 4)
              return statIsReversed ? "#F64030" : "#476F20";
            else if (d.data.group === 3)
              return statIsReversed ? "#FBA180" : "#73B240";
            else if (d.data.group === 2) return "#FCE230";
            else if (d.data.group === 1)
              return statIsReversed ? "#73B240" : "#FBA180";
            else
              return statIsReversed ? "#476F20" : "#F64030";
          });

        cell.append("path").attr("d", d => (d ? "M" + d.join("L") + "Z" : d));

        var self = this;

        cell
          .attr("player-id",(d) => d.data.id)
          .on("mouseover", function(d) {
            let radius = (d.data.id === self.playerId)
              || (self.highlightPlayersOfTeamId && self.highlightPlayersOfTeamId.includes(d.data.teamId))
              || (self.highlightPlayersByPlayerId && self.highlightPlayersByPlayerId.includes(d.data.id)) ? 14 : 7;
            self.onCircleOver({id:d.data.id, size: radius})
            d3
            .select(this)
            .selectAll("circle")
            .attr("r", function(d) {
              return radius;
            })
            .style("stroke-width", 2)
            .style("stroke", self.darkMode ? "#FFFFFF" : "#000000");

            d3.select(`body > .${self.tooltipClass}`)
              .transition()
              .duration(200)
              .style("display", "inline-block")
              .style("opacity", 0.85);
            let rankingOfPlayer = statIsReversed ? self.getRankingOfPlayer(d.data) + 1 : self.data.length - self.getRankingOfPlayer(d.data);
              d3.select(`body > .${self.tooltipClass}`)
                .style("padding","10px 40px")
                .html(
                  "<b>" +
                    _.get(d, "data.name") +
                    "</b></br>" +
                    `${d.data.tN} · ${d.data.sN}` +
                    "</br>" +
                    (_.get(d, "data.v", 0) > VALUE_OVER_WHICH_JUST_ONE_DECIMAL
                      ? _.get(d, "data.v", 0).toFixed(1)
                      : _.get(d, "data.v", 0).toFixed(2)) +
                    "</br>" +
                    `#${rankingOfPlayer} ${self.$t("general.outOf")} ${self.data.length} ${self.$t("general.players")}`
                )
                .style("width","auto")
                .attr("height",null);

                let tSize = d3.select(`body > .${self.tooltipClass}`).node().getBoundingClientRect();
                let circle = d3.select(`#beeCircle-${self.stat}-${_.get(d,'data.pSI')}`).node();
                //Get circle radius
                let circleRadius = circle.r.baseVal.value;
                let bottom10 = (rankingOfPlayer / self.data.length) >= 0.9;
                let top3 = (rankingOfPlayer / self.data.length) <= 0.03;
                if(bottom10){
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "left", `${circle.getBoundingClientRect().right + circleRadius}px`
                  )
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "top",  `${circle.getBoundingClientRect().top + window.scrollY - (tSize.height/2) - 20}px`
                  )
                }else if(top3){
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "left", `${circle.getBoundingClientRect().left - (tSize.width) - circleRadius}px`
                  )
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "top",  `${circle.getBoundingClientRect().top + window.scrollY - (tSize.height/2) - 20}px`
                  )
                }else{
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "left", `${circle.getBoundingClientRect().left  - (tSize.width / 2) + circleRadius}px`
                  )
                  d3.select(`body > .${self.tooltipClass}`).style(
                    "top",  `${circle.getBoundingClientRect().top + window.scrollY - tSize.height - 20}px`
                  )
                }
          })
          .on("mouseout", function(d) {
            d3
              .select(this)
              .selectAll("circle")
              .attr("r", (d) =>  {
                let base = (d.data.id === self.playerId)
                || ( self.comparisonPlayer &&
                  d.data.id === self.comparisonPlayer.playerId &&
                  d.data.seasonId === Number(self.comparisonPlayer.seasonId))
                || (self.highlightPlayersOfTeamId && self.highlightPlayersOfTeamId.includes(d.data.teamId))
                || (self.highlightPlayersByPlayerId && self.highlightPlayersByPlayerId.includes(d.data.id)) ? 10 : 5;
                base = self.$vuetify.breakpoint.xlOnly ? base : self.sizeOffset*base;
                self.onCircleOut({id:d.data.id,size: base})
                return base;
              })
              .style("stroke", self.darkMode ? "#000000" : "#FFFFFF")
              .style("stroke-width", 1);
            d3.select(`body > .${self.tooltipClass}`)
              .transition()
              .duration(300)
              .style("display", "none");
          })
          .on("click", function(d){
            if(!self.$vuetify.breakpoint.mobile){
              var selection = d3.select(this);

              selection
                  .selectAll("text.label")
                  .data([d])
                  .enter()
                  .append("text")
                  .attr("class", self.darkMode ? "label-dark shadow-dark" : "label shadow")
                  .style("font-size", "10px")
                  .style("font-family", this.fontFamily)
                  .attr("x", selection.data()[0].data.x + "px")
                  .attr("y", selection.data()[0].data.y + "px")
                  .html(function (d) {
                    const playerNameLengthHalved = self.measureText(selection.data()[0].data.name) / 2;
                    const teamNameLengthHalved = self.measureText(`${selection.data()[0].data.tN} · ${d.data.sN}`) / 2;
                    const statNameLengthHalved = (self.measureText("0.00") + self.measureText(`#${self.data.length - self.getRankingOfPlayer(d.data)} ${self.$t("general.outOf")} ${self.data.length}`))  / 2;
                    let rankingOfPlayer = statIsReversed ? self.getRankingOfPlayer(d.data) + 1 : self.data.length - self.getRankingOfPlayer(d.data);

                    return "<tspan>" + selection.data()[0].data.name + "</tspan>"
                         + "<tspan dy='1.2em' x=" + (selection.data()[0].data.x + playerNameLengthHalved - teamNameLengthHalved)+ ">" + `${selection.data()[0].data.tN} · ${selection.data()[0].data.sN}` + "</tspan>"
                         + "<tspan dy='1.2em' x=" + (selection.data()[0].data.x + playerNameLengthHalved - statNameLengthHalved) + ">" + (selection.data()[0].data.v > VALUE_OVER_WHICH_JUST_ONE_DECIMAL ? selection.data()[0].data.v.toFixed(1) : selection.data()[0].data.v.toFixed(2)) +
                          ` · #${rankingOfPlayer} ${self.$t("general.outOf")} ${self.data.length}` + "</tspan>"
                         ;
                    })

              if (labels.map(a => a.name).indexOf(selection.data()[0].data.name) < 0) {
                  labels.push({
                      name: selection.data()[0].data.name
                  });
              }

              cell.selectAll(self.darkMode ? ".label-dark" : ".label")
              .style("cursor", "pointer")
              .raise()
              .call(
                  d3.drag()
                  .on("drag", function(){
                      const selection = d3.select(this);
                      const dataPoint = self.data.find(p => p.pSI === selection.data()[0].data.pSI);

                      selection
                      .style("cursor", "crosshair");

                      selection
                          .attr("transform", "translate(" + (d3.event.x - dataPoint.x) + "," + (d3.event.y - dataPoint.y) + ")")

                      let path;

                      path = d3.select(selection.node().parentNode)
                          .selectAll("path")
                          .data(["M " + (d3.event.x + selection.node().getBoundingClientRect().width / 2) + ", " + Number(d3.event.y) + " A 30 30 0 0 0 " + dataPoint.x + "," + dataPoint.y], p => p);

                      path
                          .attr("d", p => p)
                          .style("fill", "none")
                          .style("stroke", "#74736c");

                      path.exit().remove();

                      path.enter()
                          .insert("path","text")
                          .attr("d", p => p)
                          .style("fill", "none")
                          .style("stroke", "#74736c");

                  })
                  .on("end", function(){
                      var selection = d3.select(this);

                      selection
                      .style("cursor", "pointer")

                      if (labels.map(a => a.name).indexOf(selection.data()[0].data.name)>=0) {
                       labels[labels.map(a => a.name).indexOf(selection.data()[0].data.name)].translate = [d3.event.x - selection.data()[0].data.x, d3.event.y - selection.data()[0].data.y];

                       labels[labels.map(a => a.name).indexOf(selection.data()[0].data.name)].path = d3.select(selection.node().parentNode).select("path").attr("d");

                      } else {
                        var dataPoint = self.data.find(p => p.pSI === selection.data()[0].data.pSI);
                        labels.push({
                            label: selection.data()[0].data.name,
                            translate: [d3.event.x - dataPoint.x, d3.event.y - dataPoint.y],
                            path: d3.select(selection.node().parentNode).select("path").attr("d")
                        });
                      }
                  })
              );
            }
            })
            .on("dblclick", function(d) {
              if(!self.$vuetify.breakpoint.mobile){
                const selection = d3.select(this);
                selection.selectAll("*:not(circle)").remove();
                labels.splice(labels.map(a => a.name).indexOf(selection.data()[0].name),1);
              }
            });
      }else{
        //Print No Data text
        svg
          .attr("width", width)
          .attr("height", height)
          .append("text")
          .attr("x", width / 2) // Coordenada X centrada horizontalmente
          .attr("y", height / 2) // Coordenada Y centrada verticalmente
          .attr("text-anchor", "middle") // Ancla el texto en el medio horizontalmente
          .attr("dominant-baseline", "middle") // Ancla el texto en el medio verticalmente
          .text(this.$t('general.noDataGivenFilters')) // Texto que quieres añadir
          .attr("font-family", this.fontFamily) // Familia de la fuente
          .attr("font-size", "24px") // Tamaño de la fuente
          .attr("fill", "#3366ff"); // Color del texto
      }
      

    },
    onResize(){
      let elto =document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    },
    getRankingOfPlayer(p){
      return _.chain(this.data).orderBy("v",'asc').map(d => {return `${d.id}-${d.seasonId}`}).indexOf(`${p.id}-${p.seasonId}`).value()
    },
    drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
      if(!this.images[idName] || !idName ){
        let base_image = new Image();
        base_image.crossOrigin = "anonymous";
        base_image.src = img;
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          x,
          y,
          width,
          height,
          dataUrl,
          idName,
          bottom
          );
        }
      }else{
        if(bottom){
          svg.node().insertBefore(this.images[idName],svg.node().firstChild);
        }else{
          svg.node().appendChild(this.images[idName]);
        }
      }
    },
    onCircleOver(evtObject){
      let el = d3
        .selectAll(`g[player-id="${evtObject.id}"]`)
        el.selectAll("circle")
        .attr("r", function(d) {
          return evtObject.size;
        })
        .style("stroke-width", 2)
        .style("stroke", this.darkMode ? "#FFFFFF" : "#000000");
    },
    onCircleOut(evtObject){
      let el = d3
        .selectAll(`g[player-id="${evtObject.id}"]`)
        el.selectAll("circle")
        .attr("r", function(d) {
          return evtObject.size;
        })
        .style("stroke-width", 1)
        .style("stroke", this.darkMode ? "#000000" : "#FFFFFF")
    }
  },
  beforeDestroy(){
    this.data = null;
    this.$forceUpdate();
    const svgObject = document.getElementById(this.rootElementId);
    const svg = d3.select(svgObject);
    svg.selectAll("*").remove();
  },
  destroyed(){
    this.data = null;
    delete this.observer;
    const svgObject = document.getElementById(this.rootElementId);
    const svg = d3.select(svgObject);
    svg.selectAll("*").remove();
  }
};

var labels = [];
</script>



// WEBPACK FOOTER //
// src/views/base/Beeswarm.vue